import React, { useMemo, useState } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps,
} from 'recharts';
import { formatCurrency } from '../../../../../utils/FormatFunctions';
import { IEvolucaoMensal } from '../../../../../hooks/useFiscalContabil';

interface TemporalEvolutionChartProps {
  data: IEvolucaoMensal[];
  isLoading: boolean;
  ano: number;
  // eslint-disable-next-line no-unused-vars
  onAnoChange: (ano: number) => void;
}

type ViewMode = 'quantidade' | 'valor';

interface EvolutionTooltipPayload {
  entradas: number;
  saidas: number;
  servicos: number;
  total: number;
  divergencias: number;
  valor_entradas: number;
  valor_saidas: number;
  valor_servicos: number;
  valor_total: number;
  valor_divergencias: number;
}

function EvolutionTooltip({
  active,
  payload,
  label,
  viewMode,
}: TooltipProps<number, string> & { viewMode: ViewMode }) {
  if (!active || !payload?.length) return null;
  const itemData = payload[0]?.payload as EvolutionTooltipPayload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      <p className="mb-2 font-semibold text-gray-800">{label}</p>
      {viewMode === 'quantidade' ? (
        <>
          <p className="text-sm text-green-600">
            Entradas: <strong>{itemData.entradas}</strong>
          </p>
          <p className="text-sm text-orange-600">
            Saídas: <strong>{itemData.saidas}</strong>
          </p>
          <p className="text-sm text-purple-600">
            Serviços: <strong>{itemData.servicos}</strong>
          </p>
          <p className="mt-1 border-t pt-1 text-sm text-gray-700">
            Total: <strong>{itemData.total}</strong>
          </p>
          {itemData.divergencias > 0 && (
            <p className="text-sm text-red-600">
              Divergências: <strong>{itemData.divergencias}</strong>
            </p>
          )}
        </>
      ) : (
        <>
          <p className="text-sm text-green-600">
            Entradas: <strong>{formatCurrency(itemData.valor_entradas)}</strong>
          </p>
          <p className="text-sm text-orange-600">
            Saídas: <strong>{formatCurrency(itemData.valor_saidas)}</strong>
          </p>
          <p className="text-sm text-purple-600">
            Serviços: <strong>{formatCurrency(itemData.valor_servicos)}</strong>
          </p>
          <p className="mt-1 border-t pt-1 text-sm text-gray-700">
            Total: <strong>{formatCurrency(itemData.valor_total)}</strong>
          </p>
          {itemData.valor_divergencias > 0 && (
            <p className="text-sm text-red-600">
              Diferença: <strong>{formatCurrency(itemData.valor_divergencias)}</strong>
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function TemporalEvolutionChart({
  data,
  isLoading,
  ano,
  onAnoChange,
}: TemporalEvolutionChartProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('quantidade');

  const chartData = useMemo(() => {
    // Preenche meses faltantes com zeros
    const mesesNome = [
      'Jan',
      'Fev',
      'Mar',
      'Abr',
      'Mai',
      'Jun',
      'Jul',
      'Ago',
      'Set',
      'Out',
      'Nov',
      'Dez',
    ];
    const dataMap = new Map(data.map(item => [item.mes, item]));

    return mesesNome.map((nome, index) => {
      const mes = index + 1;
      const item = dataMap.get(mes);
      return {
        mes_nome: nome,
        mes,
        entradas: item?.entradas || 0,
        saidas: item?.saidas || 0,
        servicos: item?.servicos || 0,
        total: item?.total || 0,
        divergencias: item?.divergencias || 0,
        valor_entradas: item?.valor_entradas || 0,
        valor_saidas: item?.valor_saidas || 0,
        valor_servicos: item?.valor_servicos || 0,
        valor_total: item?.valor_total || 0,
        valor_divergencias: item?.valor_divergencias || 0,
      };
    });
  }, [data]);

  const totais = useMemo(() => {
    return chartData.reduce(
      (acc, item) => ({
        entradas: acc.entradas + item.entradas,
        saidas: acc.saidas + item.saidas,
        servicos: acc.servicos + item.servicos,
        total: acc.total + item.total,
        divergencias: acc.divergencias + item.divergencias,
        valor_total: acc.valor_total + item.valor_total,
        valor_divergencias: acc.valor_divergencias + item.valor_divergencias,
      }),
      {
        entradas: 0,
        saidas: 0,
        servicos: 0,
        total: 0,
        divergencias: 0,
        valor_total: 0,
        valor_divergencias: 0,
      },
    );
  }, [chartData]);

  // Anos disponiveis: deriva sempre os ultimos 5 anos a partir de hoje.
  // Se quisermos no futuro respeitar so anos com dados, expor via endpoint
  // /anos-disponiveis no backend.
  const currentYear = new Date().getFullYear();
  const years = useMemo(
    () => [currentYear - 4, currentYear - 3, currentYear - 2, currentYear - 1, currentYear],
    [currentYear],
  );

  const formatYAxis = (value: number) => {
    if (viewMode === 'valor') {
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
      return value.toString();
    }
    return value.toString();
  };

  return (
    <div className="flex h-full flex-col rounded-xl bg-white p-4 shadow-md">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2">
        <div className="flex items-center gap-2">
          <svg
            className="h-4 w-4 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
            />
          </svg>
          <h3 className="text-sm font-semibold text-gray-700">Evolução Mensal</h3>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 p-0.5">
            <button
              type="button"
              onClick={() => setViewMode('quantidade')}
              className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                viewMode === 'quantidade'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Qtd
            </button>
            <button
              type="button"
              onClick={() => setViewMode('valor')}
              className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                viewMode === 'valor' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Valor
            </button>
          </div>

          <select
            value={ano}
            onChange={e => onAnoChange(Number(e.target.value))}
            className="rounded border border-gray-200 px-2 py-1 text-xs text-gray-700 focus:border-blue-500 focus:outline-none"
          >
            {years.map(y => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Resumo do ano */}
      <div className="mb-3 flex flex-wrap gap-3 text-[10px]">
        <div className="flex items-center gap-1 rounded bg-gray-50 px-2 py-1">
          <span className="text-gray-500">Total:</span>
          <span className="font-bold text-gray-700">{totais.total}</span>
          <span className="text-gray-400">|</span>
          <span className="font-semibold text-gray-600">{formatCurrency(totais.valor_total)}</span>
        </div>
        <div className="flex items-center gap-1 rounded bg-green-50 px-2 py-1">
          <div className="h-2 w-2 rounded-full bg-green-500" />
          <span className="text-green-700">{totais.entradas}</span>
        </div>
        <div className="flex items-center gap-1 rounded bg-orange-50 px-2 py-1">
          <div className="h-2 w-2 rounded-full bg-orange-500" />
          <span className="text-orange-700">{totais.saidas}</span>
        </div>
        <div className="flex items-center gap-1 rounded bg-purple-50 px-2 py-1">
          <div className="h-2 w-2 rounded-full bg-purple-500" />
          <span className="text-purple-700">{totais.servicos}</span>
        </div>
        {totais.divergencias > 0 && (
          <div className="flex items-center gap-1 rounded bg-red-50 px-2 py-1">
            <div className="h-2 w-2 rounded-full bg-red-500" />
            <span className="text-red-700">
              {totais.divergencias} div. | {formatCurrency(totais.valor_divergencias)}
            </span>
          </div>
        )}
      </div>

      <div
        className="min-h-[280px] flex-1"
        role="img"
        aria-label={`Evolução mensal ${ano}: barras com quantidade ou valor por tipo (entradas, saídas, serviços) e linha de divergências`}
      >
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mes_nome" tick={{ fontSize: 10, fill: '#6B7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} tickFormatter={formatYAxis} />
              <Tooltip content={<EvolutionTooltip viewMode={viewMode} />} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />

              {viewMode === 'quantidade' ? (
                <>
                  <Bar
                    dataKey="entradas"
                    name="Entradas"
                    stackId="a"
                    fill="#38A169"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="saidas"
                    name="Saídas"
                    stackId="a"
                    fill="#DD6B20"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="servicos"
                    name="Serviços"
                    stackId="a"
                    fill="#805AD5"
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    name="Total"
                    stroke="#1976D2"
                    strokeWidth={2}
                    dot={{ fill: '#1976D2', r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="divergencias"
                    name="Divergências"
                    stroke="#E53E3E"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#E53E3E', r: 3 }}
                  />
                </>
              ) : (
                <>
                  <Bar
                    dataKey="valor_entradas"
                    name="Entradas"
                    stackId="a"
                    fill="#38A169"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="valor_saidas"
                    name="Saídas"
                    stackId="a"
                    fill="#DD6B20"
                    radius={[0, 0, 0, 0]}
                  />
                  <Bar
                    dataKey="valor_servicos"
                    name="Serviços"
                    stackId="a"
                    fill="#805AD5"
                    radius={[4, 4, 0, 0]}
                  />
                  <Line
                    type="monotone"
                    dataKey="valor_total"
                    name="Total"
                    stroke="#1976D2"
                    strokeWidth={2}
                    dot={{ fill: '#1976D2', r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="valor_divergencias"
                    name="Diferença"
                    stroke="#E53E3E"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={{ fill: '#E53E3E', r: 3 }}
                  />
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
